import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Target, TrendingUp, AlertTriangle, DollarSign } from 'lucide-react';
import { PathResult, PathMatchScore, VersionConfig } from '@/types/assessment';

interface PathComparisonProps {
  paths: PathResult[];
  matches: PathMatchScore[];
  shortlisted: number[];
  config: VersionConfig;
  onBack: () => void;
}

const PathComparison = ({ paths, matches, shortlisted, config, onBack }: PathComparisonProps) => {
  const isHighSchool = config.edition === 'highschool';
  const getPathName = (i: number) => paths[i]?.name || `Path ${i + 1}`;
  const getMatch = (i: number) => matches.find(m => m.pathIndex === i);

  const cols = shortlisted.length;

  // Winner = shortlisted path with the highest match score. Used to give
  // the comparison a clear visual hierarchy instead of N equal cards.
  const ranked = [...shortlisted].sort(
    (a, b) => (getMatch(b)?.matchPercent || 0) - (getMatch(a)?.matchPercent || 0)
  );
  const winnerIdx = ranked[0];
  const runnerUpIdx = ranked[1];

  // Factual gap sentence. Only assert what the data supports: the score
  // delta, and the runner-up's named skill gaps (real, from PathMatchScore).
  let gapSentence: string | null = null;
  if (winnerIdx !== undefined && runnerUpIdx !== undefined) {
    const wPct = getMatch(winnerIdx)?.matchPercent || 0;
    const rPct = getMatch(runnerUpIdx)?.matchPercent || 0;
    const delta = wPct - rPct;
    const runnerGaps = (getMatch(runnerUpIdx)?.skillGaps || []).map(g => g.skill).slice(0, 2);
    if (delta === 0) {
      gapSentence = `${getPathName(winnerIdx)} and ${getPathName(runnerUpIdx)} are effectively tied. The deciding factor is which day-to-day work you'd rather do, not the score.`;
    } else {
      gapSentence = `${getPathName(winnerIdx)} leads ${getPathName(runnerUpIdx)} by ${delta} point${delta === 1 ? '' : 's'}.` +
        (runnerGaps.length > 0
          ? ` The gap is mostly ${runnerGaps.join(' and ')} — close those and ${getPathName(runnerUpIdx)} becomes just as strong.`
          : '');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h3 className="font-display font-semibold text-foreground">Step 3: Compare Paths</h3>
      </div>

      {/* Header row — winner emphasized, others de-emphasized */}
      <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {shortlisted.map(idx => {
          const match = getMatch(idx);
          const isWinner = idx === winnerIdx;
          return (
            <div
              key={idx}
              className={`glass-card p-4 text-center space-y-2 transition-all ${
                isWinner
                  ? 'border-primary/50 ring-2 ring-primary/30 bg-primary/[0.04]'
                  : 'opacity-70'
              }`}
            >
              {isWinner && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-wider">
                  Strongest fit
                </span>
              )}
              <h4 className={`font-display font-bold ${isWinner ? 'text-primary' : 'text-muted-foreground'}`}>
                {getPathName(idx)}
              </h4>
              <div className={`text-2xl font-display font-bold ${isWinner ? 'text-foreground' : 'text-muted-foreground'}`}>
                {match?.matchPercent}%
              </div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">match score</p>
            </div>
          );
        })}
      </div>

      {/* Factual tipping-point sentence */}
      {gapSentence && (
        <div className="rounded-xl border border-border/50 bg-card/50 px-4 py-3">
          <p className="text-xs text-muted-foreground leading-relaxed">{gapSentence}</p>
        </div>
      )}

      {/* Comparison rows */}
      {[
        {
          icon: Target,
          title: 'Approach',
          render: (idx: number) => paths[idx].description,
        },
        {
          icon: TrendingUp,
          title: 'Key Milestones',
          render: (idx: number) => {
            const firstTimeline = config.timelines[0];
            const actions = paths[idx].actions[firstTimeline] || [];
            return actions.slice(0, 3).join(' • ');
          },
        },
        {
          icon: Clock,
          title: 'Effort Level',
          render: (idx: number) =>
            idx === 0 ? 'Steady & structured' : idx === 1 ? 'Moderate intensity' : 'High commitment',
        },
        {
          icon: AlertTriangle,
          title: 'Skill Gaps',
          render: (idx: number) => {
            const match = matches.find(m => m.pathIndex === idx);
            return match?.skillGaps.map(g => g.skill).join(', ') || 'None identified';
          },
        },
      ].map((row, rowIdx) => (
        <motion.div
          key={row.title}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: rowIdx * 0.05 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2">
            <row.icon className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-foreground">{row.title}</span>
          </div>
          <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {shortlisted.map(idx => (
              <div key={idx} className="glass-card p-3">
                <p className="text-xs text-muted-foreground leading-relaxed">{row.render(idx)}</p>
              </div>
            ))}
          </div>
        </motion.div>
      ))}

      {/* Projects to Build (HS) / Monetize Now (college, professional) */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-accent" />
          <span className="text-sm font-medium text-foreground">
            {isHighSchool ? 'Projects to Build' : 'Monetize Now'}
          </span>
        </div>
        <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {shortlisted.map(idx => (
            <div key={idx} className="glass-card p-3 space-y-1">
              {paths[idx].monetizeNow.slice(0, 2).map((item, i) => (
                <p key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                  <span className="text-accent shrink-0">▸</span> {item}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PathComparison;
